import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import deepEqual from 'fast-deep-equal/es6/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ContentSkip,
  Navbar,
  NavbarActionButtons,
  SidebarCollapse,
  SpinnerModal,
  setUserAlias,
  removeUserAlias,
  getUserAlias,
  AgentTermsModal,
} from 'shared-components';
import { useSession } from 'shared-components/authentication';
import { ApplicationState } from '../../../store';
import { hasPerm, P } from '../../../utilities/roles/permissions';
import CustomDataGrid from '../../organisms/CASDataGrid';
import { LeftBar } from './left-bar/index';
import { DEFAULT_STAGE_TAB } from '../../../utilities/constants';
import { clearStudents } from '../../../store/dashboard/actions';
import { acceptTerms as authActions_acceptTerms, logout } from '../../../store/auth/actions';
import { UserRole } from '../../../store/auth/types';
import { getConfigRecord, getAdmAllowedAliases } from '../../../store/config/actions';
import { setUniConfigLoaded } from '../../../store/data/actions';
import {
  getAuth,
  getHasAgentOnlyDocs,
  getHasPathwayids,
  getIsAdminOrEnroly,
  getIsImmigrationProvider,
  getIsOfferEnabled,
  getIsPartner,
  getPartners,
  getRole,
  getSelectedRows,
  getTabs,
} from '../../../store/selectors';

import { getActiveStageTab } from '../../../utilities/helpers';
import { getColumnsForActiveTab } from '../../organisms/CASDataGrid/grid-columns';

const SIDEBAR_STORAGE_KEY = 'sidebarIsOpen';

const UniDashboardPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const session = useSession();

  const isAcceptedTerms = useSelector(({ auth }: ApplicationState) => auth.terms);
  const groups = useSelector(({ auth }: ApplicationState) => auth.groups);

  const isLoading = useSelector(({ ui }: ApplicationState) => ui.isLoading);
  const uni = useSelector((state: ApplicationState) => state.auth.uni);
  const isUniConfigLoaded = useSelector((state: ApplicationState) => state.data.isUniConfigLoaded);
  const prevUniRef = useRef(uni);

  const acceptTerms = useCallback(() => dispatch(authActions_acceptTerms()), [dispatch]);
  const logOut = useCallback(() => dispatch(logout()), [dispatch]);
  const onLogout = async () => {
    logOut();
    await session.onLogout();
  };
  const params = new URLSearchParams(window.location.search);
  const headerTab = useSelector(({ ui }: ApplicationState) => ui.headerTab, deepEqual);
  const tabs = useSelector(getTabs);
  const userRole = useSelector(getRole);
  const isPathway = useSelector(getHasPathwayids);
  const activeTab = getActiveStageTab(params, headerTab);
  const partners = useSelector(getPartners);
  const isEnrolyUser = useSelector(getIsAdminOrEnroly);
  const isServiceProvider = useSelector(getIsPartner);
  const isImmigrationProvider = useSelector(getIsImmigrationProvider);
  const isOfferEnabled = useSelector(getIsOfferEnabled);
  const selectedRows = useSelector(getSelectedRows);
  const hasAgentOnlyDocs = useSelector(getHasAgentOnlyDocs);
  const isAgentUser = [UserRole.Agent, UserRole.AgencyAdmin].includes(userRole) ?? false;

  const canViewInsights = hasPerm(userRole, P.VIEW_INSIGHTS) || groups.includes('Insights');
  const canViewConfig = hasPerm(userRole, P.VIEW_CONFIG);

  const auth = useSelector(getAuth);
  const allowedAliases = useSelector((state: ApplicationState) => state.config.allowedAliases);
  const admAllowedAliases = useSelector((state: ApplicationState) => state.config.admAllowedAliases);
  const currentUser = useSelector((state: ApplicationState) => state.auth.currentUser);
  const username = useSelector((state: ApplicationState) => state.auth.student_number);

  // Determine which aliases to use based on role
  const isAdminOrEnroly = userRole === UserRole.Admin || userRole === UserRole.Enroly;
  const activeAliases = isAdminOrEnroly ? admAllowedAliases || [] : allowedAliases || [];

  // Build Act As options
  const currentUserName = useMemo(() => {
    return currentUser?.givenName && currentUser?.familyName
      ? `${currentUser.givenName} ${currentUser.familyName}`
      : username || '';
  }, [currentUser, username]);

  const actAsOptions = useMemo(() => {
    return activeAliases && activeAliases.length > 0 ? [currentUserName, ...activeAliases].filter(Boolean) : [];
  }, [activeAliases, currentUserName]);

  const currentAlias = getUserAlias() || currentUserName;

  const handleActAsChange = useCallback(
    (selectedAlias: string) => {
      if (selectedAlias === currentUserName) {
        removeUserAlias();
      } else {
        setUserAlias(selectedAlias);
      }
      // Reload the page to apply the alias change
      window.location.reload();
    },
    [currentUserName],
  );

  // Fetch ADM aliases for Admin/Enroly users on mount
  useEffect(() => {
    if (isAdminOrEnroly) {
      dispatch(getAdmAllowedAliases());
    }
  }, [isAdminOrEnroly, dispatch]);

  const viableSortKeys = getColumnsForActiveTab({
    userRole,
    activeTab,
    tabs,
    partners,
    isServiceProvider,
    isEnrolyUser,
    isImmigrationProvider,
    isAgentUser,
    hasAgentOnlyDocs,
    isOfferEnabled,
    width: 0,
    isPathway,
    selectedRows,
  }).map(c => c.key);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (uni) {
      dispatch(getConfigRecord(uni));

      // Only clear students if uni actually changed (not on initial mount or navigation)
      if (prevUniRef.current && prevUniRef.current !== uni) {
        dispatch(clearStudents());
      }

      prevUniRef.current = uni;
    }
    return () => {
      dispatch(setUniConfigLoaded(false));
    };
  }, [uni, dispatch]);

  useEffect(() => {
    const sortCol = searchParams.get['sort-col'];
    if (sortCol && !viableSortKeys.includes(sortCol)) {
      // remove from params
      searchParams.delete('sort-col');
      searchParams.delete('sort-dir');

      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, viableSortKeys]);

  useEffect(() => {
    if (isUniConfigLoaded) {
      const params = new URLSearchParams(location.search);
      if (!params.toString().length) {
        params.set('page', '1');
        params.set('stage', DEFAULT_STAGE_TAB);

        navigate(`${location.pathname}?${params.toString()}`);
      }
      // Remove programmable tags GET call, it's done in CASToolbar component
      // dispatch(programmableTagsActions.getProgrammableTagRecords());
      // Remove the unconditional requestFilters() call - let CASDataGrid handle it
      // dispatch(requestFilters());
      // Remove the unconditional requestStudents() call - let CASDataGrid handle it
      // dispatch(requestStudents());
    }
  }, [isUniConfigLoaded, location.pathname, auth.intake, auth.uni]);

  const sidebarDefaultState = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(SIDEBAR_STORAGE_KEY) || 'true');
    } catch {
      return true;
    }
  }, []);

  // Get sidebar collapsed state from localStorage
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: sidebarDefaultState });

  // Update localStorage whenever isOpen changes
  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(isOpen));
  }, [isOpen]);

  return (
    <Box
      bgColor="chakra-body-bg"
      position="absolute"
      left={{ base: 0, md: 'navbarWidth' }}
      width={{ base: '100%', md: 'calc(100vw - var(--ds-sizes-navbarWidth))' }}
      height={'100vh'}
    >
      <SpinnerModal show={isLoading} />

      <ContentSkip />

      <div id="content-loaded" data-testid="uni-dashboard" data-selector="uni-dashboard" />

      <Navbar
        actionableButtonsStates={{
          [NavbarActionButtons.STUDENTS]: {
            isVisible: true,
            isSelected: true,
          },
          [NavbarActionButtons.INSIGHTS]: {
            isVisible: canViewInsights,
          },
          [NavbarActionButtons.SETTINGS]: {
            isVisible: canViewConfig,
          },
          [NavbarActionButtons.FEEDBACK]: {
            isVisible: true,
          },
        }}
        username={currentUserName}
        isAuthenticated={true}
        onLogout={onLogout}
        subnav={<LeftBar />}
        actAs={
          actAsOptions.length > 1
            ? {
                options: actAsOptions,
                currentValue: currentAlias,
                onChange: handleActAsChange,
              }
            : undefined
        }
      />

      <Flex overflowX="auto" overflowY="hidden" width={'100%'} height={'100%'} data-selector="content-wrap">
        <SidebarCollapse
          label="Stages navigation"
          isOpen={isOpen}
          onToggle={onToggle}
          containerProps={{ height: '100%' }}
        >
          <LeftBar />
        </SidebarCollapse>

        <Flex as="main" id="main" height="100%" overflow="hidden" flexDirection="column" flexGrow={1}>
          <CustomDataGrid />
        </Flex>
      </Flex>

      {!isAcceptedTerms && !isLoading && <AgentTermsModal onAcceptTerms={acceptTerms} />}
    </Box>
  );
};

export default UniDashboardPage;
